<template>
  <teleport
    to="#vue-app"
    :disabled="!isMounted"
  >
    <transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="translate-y-full opacity-0 md:translate-y-0 md:translate-x-full"
      enter-to-class="translate-y-0 opacity-100 md:translate-x-0"
      leave-active-class="transition-all duration-300 ease-in"
      leave-from-class="translate-y-0 opacity-100 md:translate-x-0"
      leave-to-class="translate-y-full opacity-0 md:translate-y-0 md:translate-x-full"
    >
      <div
        v-if="showPopup && !isSideCartOpen && isMounted"
        class="fixed inset-x-0 bottom-0 z-50 w-full rounded-t-lg bg-white p-2 shadow-lg md:inset-x-auto md:bottom-auto md:right-4 md:top-32 md:w-80 md:rounded-lg md:p-4"
      >
        <div class="hidden items-start justify-between md:flex">
          <h3 class="text-lg font-medium text-gray-900">Añadido al carrito</h3>
          <button
            class="text-gray-400 hover:text-gray-600"
            @click="closePopup"
          >
            <XMarkIcon class="size-5" />
          </button>
        </div>
        <div class="mt-3 flex items-center space-x-3">
          <img
            class="size-16 shrink-0 rounded-md bg-cover bg-center"
            :src="productImageToShow"
          />
          <div class="min-w-0 flex-1">
            <p class="truncate text-sm font-medium text-gray-900">
              {{ cartStore.recentlyAddedItem?.product?.name }}
            </p>
            <p class="text-sm text-gray-500">Cantidad: {{ cartStore.recentlyAddedItem?.quantity }}</p>
            <p class="text-sm text-gray-900">{{ useFormatCurrency(totalPrice) }}</p>
          </div>
          <button
            class="text-gray-400 hover:text-gray-600 md:hidden"
            @click="closePopup"
          >
            <XMarkIcon class="size-5" />
          </button>
        </div>
        <div
          v-if="makerId"
          class="mt-4"
        >
          <minimum-purchase-progress-bar
            :maker-id="makerId"
            :items="makerItems"
          />
        </div>
        <div class="mt-4">
          <a
            href="/orders/new"
            class="block w-full rounded-md border border-lk-green bg-white px-3 py-2 text-center text-sm text-lk-green hover:bg-lk-green hover:text-white"
          >
            Ver carrito
          </a>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { computed, watch, inject, type Ref, ref, onMounted } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/solid';
import useCartStore from 'stores/cart';
import useFormatCurrency from './use/format-currency';
import MinimumPurchaseProgressBar from './shared/minimum-purchase-progress-bar.vue';
import useGeneralMenuStore from 'stores/general-menu-store';

const AUTO_CLOSE_DELAY = 5000;
const MOBILE_AUTO_CLOSE_DELAY = 1500;
const isMobile = inject('isMobile') as Ref<boolean>;
const cartStore = useCartStore();
const menuStore = useGeneralMenuStore();
const windowPath = computed(() => window.location.pathname);
const showPopup = computed(() => cartStore.showAddedToCartPopup && !windowPath.value.includes('/orders/new'));
const isSideCartOpen = computed(() => menuStore.showCartSidebar);
const makerId = computed(() => cartStore.recentlyAddedItem?.product?.makerId || 0);

const productImageToShow = computed(() => {
  if (!cartStore.recentlyAddedItem?.product) return '';
  return cartStore.recentlyAddedItem.product.images[0]?.derivatives?.jpgMd?.url || '';
});

const makerItems = computed(() => {
  if (!cartStore.recentlyAddedItem?.product) return [];
  return Object.values(cartStore.items).filter(
    (item) => item.product.makerId === cartStore.recentlyAddedItem?.product?.makerId,
  );
});

const totalPrice = computed(() => {
  if (!cartStore.recentlyAddedItem?.product || !cartStore.recentlyAddedItem?.quantity) return 0;
  return cartStore.recentlyAddedItem.wholesalePriceCents * cartStore.recentlyAddedItem.quantity;
});

function closePopup() {
  cartStore.hideAddedToCartPopup();
}

watch(
  () => cartStore.showAddedToCartPopup,
  (isVisible) => {
    if (isVisible) {
      const delay = isMobile?.value ? MOBILE_AUTO_CLOSE_DELAY : AUTO_CLOSE_DELAY;
      setTimeout(() => {
        cartStore.hideAddedToCartPopup();
      }, delay);
    }
  },
);

const isMounted = ref(false);

onMounted(() => {
  isMounted.value = true;
});
</script>
